<div class="panel">
  <div class="border-b border-gray-200">
    <div class="px-8 h-20 items-center flex gap-3">
      <app-dropdown-label
        appDPSheetsContextMenu
        (appDPContextMenuOpened)="contextMenuOpened = $event"
        (appDPContextMenuFilterChanged)="selectFilter($event)"
        [collapsed]="!contextMenuOpened"
        >
        {{ collectionTitleOf(selectedFilter) }}
      </app-dropdown-label>

      <div class="grow"></div>

      @if (listTab() !== 'sheetFolders') {
        <app-context-menu-state-indicator
          svgIcon="filters"
          appSheetsListContextMenu
          [appSheetsListContextMenuWithAdvancedSearch]="true"
          />
      }
      @if (listTab() === 'sheetFolders') {
        <app-context-menu-state-indicator
          svgIcon="filters"
          appSheetFoldersListContextMenu
          [appSheetFoldersListContextMenuWithAdvancedSearch]="true"
          />
      }

      <app-search-state-indicator
        [applied]="isSearchApplied()"
        (clear)="clearSearch()"
      >
        <app-icon
          class="max-w-8 max-h-8 text-gray-500 cursor-pointer items-center"
          name="search-small"
          stchPopover
          stchPopoverPlacement="bottomRight"
          stchPopoverTrigger="click"
          [stchPopoverContent]="searchPopover"
          [stchPopoverFallbackPlacements]="[]"
          [stchPopoverShowUntil]="popoverClose"
        />
      </app-search-state-indicator>

      <!-- SBUX-1043: the panel's Add, beside search, as Wook's screenshot has it. The same menu the
           navbar and Slides' section header use, so the panel offers what the rest of the app does
           rather than a Sheets-only list. It hides the current app's own type, which is why there is
           no "New Workbook" here — the "+ New workbook" bar below covers that. It creates items; it
           does not connect them to anything, which is what that menu has always done. -->
      <app-nav-add-menu [app]="Application.sheets" />
    </div>
    @if (listState === 'tabs' && !openedFolder()) {
      <app-tabs
        class="panel-heading-tabs"
        [values]="tabs"
        [(value)]="selectedTab"
        />
    }

    <!-- SBUX-1043: the folder being browsed heads its own list, with the way back to Folders
         immediately to its left — not a row of links under the search, which is where it was in an
         earlier draft of the prototype. -->
    @if (openedFolder(); as folder) {
      <div class="panel-folder-heading">
        <app-button2
          appearance="secondary-ghost"
          size="s"
          data-test="sheets-panel-leave-folder"
          [ariaLabel]="S['Back to folders']"
          [matTooltip]="S['Back to folders']"
          (click)="leaveFolder()"
        >
          <app-icon name="chevron-left" />
        </app-button2>
        <span class="panel-folder-title" [title]="folder.title">{{ folder.title }}</span>
      </div>
    }
  </div>

  @if (listTab() === 'sheets' && creationAllowed() && !openedWorkbook()) {
    <!-- SBUX-1043: as Slides' panel does it (`panel-deck-create`), on Oleg's instruction
         (2026-10-10): the click opens a menu and Blank creates outright, and a DROP creates the
         workbook and then stitches it to what was dropped — "a drag and drop always must create a
         stitch". The shared quick form this replaced only pre-filled a form and stitched nothing.
         Inside a folder the new workbook is filed there, which the quick form's Save also did. -->
    <app-panel-sheet-create
      class="panel-quick-form"
      [sheetFolderId]="openedFolder()?.id ?? null"
      [beforeCreate]="mayCreate"
      (created)="openCreated($event)"
      (templateRequested)="selectFilter('templates')"
      (importRequested)="importOpen.set(true)"
      />
    }

    @if (listTab() === 'sheetFolders' && creationAllowed() && !openedWorkbook()) {
      <app-quick-form
        class="panel-quick-form" trigger="button" [collapseKey]="selectedFilter"
        placeholder="Add New Folder"
        >
        <app-sheet-folder-quick-form [showMore]="true" (more)="handleNewSheetFolder($event)" />
        </app-quick-form>
      }

      <div class="panel-body">
      <!-- `inert` while a workbook is over it: the list is still in the page, so without this Tab
           walks into rows nobody can see. -->
      <div
        class="panel-list border-t border-gray-200"
        [class.panel-list--covered]="openedWorkbook()"
        [attr.inert]="openedWorkbook() ? '' : null"
        >
        @if (listTab() !== 'sheetFolders') {
          <app-sheets-list
            [filters]="sheetsFilters"
            [showLocationBadge]="isAdvancedSearchApplied()"
            (openSheet)="openSheet($event)"
            (clickSheet)="openSheet($event)"
            (doubleClickSheet)="openSheet($event)"
            />
        }

        @if (listTab() === 'sheetFolders') {
          <app-sheet-folders-list
            [filters]="sheetFoldersFilters"
            (openSheetFolder)="openSheetFolder($event)"
            (clickSheetFolder)="openSheetFolder($event)"
            (openSheet)="openSheetInModal($event)"
            (doubleClickSheetFolder)="openSheetFolder($event)"
            />
        }
      </div>

      <!-- SBUX-1043: the open workbook, over the list rather than instead of it, so the list keeps its
           scroll position and its loaded page for the way back. -->
      @if (openedWorkbook(); as workbook) {
        <div class="panel-workbook">
          <!-- A labelled row of its own, as the Slides panel does it: the form's heading right below
               has its own back chevron and its own X, and a bare arrow here would read as that one. -->
          <div class="panel-workbook-back">
            <app-button2
              class="max-w-full"
              appearance="secondary-text"
              size="s"
              align="start"
              data-test="sheets-panel-back"
              [ariaLabel]="backLabel()"
              [matTooltip]="backLabel()"
              (click)="closeWorkbook()"
            >
              <app-icon name="chevron-left" />
              <span class="truncate">{{ backLabel() }}</span>
            </app-button2>
          </div>

          <!-- The workbook's header and its grid. Its own X is "close workbook": it returns to the
               list, not to the DP's form stack, and the app bar's X above it still closes the panel. -->
          <app-sheets-panel-workbook
            class="panel-workbook-form"
            [workbook]="workbook"
            [tab]="openTab()"
            (tabChanged)="rememberTab($event)"
            [sheetId]="openSheetId()"
            [cell]="openCell()"
            (activeSheetChanged)="rememberSheet($event)"
            (activeCellChanged)="rememberCell($event)"
            (closed)="closeWorkbook()"
            (created)="openCreated($event)"
            (openedInMain)="openInMainApp()"
            />
        </div>
      }
      </div>
    </div>

<ng-template #searchPopover>
  <app-sheets-search-popover
    [width]="600"
    (onSearch)="handleSearch()"
    (onCancel)="handleCancel()"
  />
</ng-template>

<!-- SBUX-1043, handoff 7 October: "Import spreadsheet" is one of the panel's list actions. The same
     dialog the nav bar's own import uses, and the same code behind it (`importAndOpenWorkbook`); what
     differs is where the workbook lands — this panel, and the folder it was browsing. -->
@if (importOpen()) {
  <app-import-dialog [busy]="importBusy()" (chosen)="importChosen($event)" (cancelled)="importOpen.set(false)" />
}
